import { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Layers, Plus, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

export const ManageClustersDialog = ({ open, onOpenChange, onChanged }) => {
  const [clusters, setClusters] = useState([]);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState(null);
  const [renameTo, setRenameTo] = useState("");

  const load = () => api.get("/clusters").then((r) => setClusters(r.data));
  useEffect(() => { if (open) load(); }, [open]);

  const create = async () => {
    try {
      await api.post("/clusters", { name: newName });
      toast.success(`Cluster "${newName.trim()}" created`);
      setNewName("");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const rename = async (oldName) => {
    try {
      const { data } = await api.put(`/clusters/${encodeURIComponent(oldName)}`, { name: renameTo });
      toast.success(`Renamed to "${data.name}" — ${data.hotels_moved} hotels & ${data.users_moved} users moved`);
      setRenaming(null);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const remove = async (name) => {
    try {
      await api.delete(`/clusters/${encodeURIComponent(name)}`);
      toast.success(`Cluster "${name}" deleted`);
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid="manage-clusters-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="w-5 h-5 text-[#14532D]" />Manage Clusters</DialogTitle>
          <DialogDescription>Create clusters here, then assign hotels to them from the hotel list. Renaming moves all its hotels and cluster heads; a cluster can only be deleted when empty.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="new-cluster-input" placeholder="New cluster name, e.g. North" value={newName}
            onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && newName.trim() && create()} />
          <Button data-testid="create-cluster-button" className="bg-[#14532D] hover:bg-[#166534] shrink-0" disabled={!newName.trim()} onClick={create}>
            <Plus className="w-4 h-4 mr-1.5" />Create
          </Button>
        </div>
        <div className="space-y-1.5">
          {clusters.map((c) => (
            <div key={c.name} className="flex items-center gap-2 border border-[#E7E5DF] rounded-lg px-3 py-2" data-testid={`cluster-row-${c.name}`}>
              {renaming === c.name ? (
                <>
                  <Input data-testid={`rename-cluster-input-${c.name}`} className="h-8 text-sm" value={renameTo} onChange={(e) => setRenameTo(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && renameTo.trim() && rename(c.name)} autoFocus />
                  <button data-testid={`rename-cluster-save-${c.name}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" onClick={() => rename(c.name)}><Check className="w-4 h-4" /></button>
                  <button className="p-1.5 rounded hover:bg-stone-100 text-stone-500" onClick={() => setRenaming(null)}><X className="w-4 h-4" /></button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-medium text-stone-800">{c.name}</span>
                  <span className="text-xs text-stone-400 font-mono">{c.hotels} hotel{c.hotels === 1 ? "" : "s"}</span>
                  <button data-testid={`rename-cluster-${c.name}`} className="p-1.5 rounded hover:bg-stone-100 text-stone-500" title="Rename"
                    onClick={() => { setRenaming(c.name); setRenameTo(c.name); }}><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button data-testid={`delete-cluster-${c.name}`} className="p-1.5 rounded hover:bg-rose-50 text-rose-500 disabled:opacity-30" title={c.hotels ? "Reassign hotels first" : "Delete"}
                    disabled={!!c.hotels} onClick={() => remove(c.name)}><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </>
              )}
            </div>
          ))}
          {clusters.length === 0 && <p className="text-sm text-stone-400 text-center py-6">No clusters yet — create the first one above.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
